import { useEffect, useMemo, useState } from "react";

import type { Location } from "history";
import { Helmet } from "react-helmet";

import { Button, Checkbox } from "@alpacahq/ui";
import { Spinner } from "@alpacahq/ui/old";

import { UserActions } from "~/v2/api/auth";
import {
  authorize,
  type OAuthAuthorizeRequest,
  type OAuthClientResponse,
  postOAuthClient,
  token,
} from "~/v2/api/oauth";
import { useAccountContext } from "~/v2/providers/AccountProvider";

type AuthorizationPageProps = {
  location?: Location;
};

type Client = OAuthClientResponse;

const AuthorizationPage = ({ location }: AuthorizationPageProps) => {
  const [client, setClient] = useState<Client>();
  const [error, setError] = useState<string>();
  const [submitted, setSubmitted] = useState(false);
  const [liveAccountId, setLiveAccountId] = useState<string>();
  const [paperAccountId, setPaperAccountId] = useState<string>();

  const { liveAccounts, paperAccounts, paperAccountsNoDetail } =
    useAccountContext();

  const { pathname, search } = location || window.location;

  const { req, prompt, env, scopes } = useMemo(() => {
    const s = new URLSearchParams(search);

    const req: OAuthAuthorizeRequest = {
      response_type: s.get("response_type") ?? "",
      client_id: s.get("client_id") ?? "",
      redirect_uri: s.get("redirect_uri") ?? "",
      scope: s.get("scope") ?? "",
      state: s.get("state") ?? "",
    };
    const prompt = s.get("prompt");
    const env = s.get("env");
    const scopes = s.get("scope")?.split(" ") ?? [];
    return {
      req,
      prompt,
      env,
      scopes,
    };
  }, [search]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: this effect performs the OAuth redirect/logout side effect and must run only when the URL changes. env, prompt, req and scopes are read from the closure deliberately: prompt and req derive from the same search string already listed, and re-running on their identity would repeat the redirect.
  useEffect(() => {
    if (
      prompt === "login" &&
      window.sessionStorage?.getItem("oauth_login") !== "1"
    ) {
      window.sessionStorage?.setItem("requested_path", pathname + search);
      UserActions.userLogout();
      return;
    }

    if (env && env !== "live" && env !== "paper") {
      setError("The requested environment is not valid.");
      return;
    }

    postOAuthClient(req)
      .then((client: Client) => {
        // check if scopes includes any scope with "identity" as prefix
        const identityScope = scopes.find((scope) =>
          scope.startsWith("identity:"),
        );
        if (identityScope && !client.ria) {
          setError(
            "This application is not authorized to request access to your personal information.",
          );
          return;
        }
        setClient(client);
      })
      .catch((e: Error) => {
        switch (e.message) {
          case "invalid_request":
            return setError("The redirect uri included is not valid.");
          case "invalid_scope":
            return setError(
              "The requested scope is invalid, unknown, or malformed.",
            );
          case "unsupported_response_type":
            return setError(
              "The authorization server does not support this response type.",
            );
          default:
            return setError(
              "Client authentication failed due to unknown client, no client authentication included, or unsupported authentication method.",
            );
        }
      });
  }, [search, pathname]);

  const redirect = (params: Record<string, string> = {}) => {
    const redirect_uri = req?.redirect_uri;

    // also checked in backend by GET /oauth/client
    const authorizedRedirectUris = client?.redirect_uri ?? [];
    if (!authorizedRedirectUris.includes(redirect_uri)) {
      setError("The redirect uri included is not valid.");
      return;
    }

    if (req.state) {
      params.state = req.state;
    }

    window.location.href =
      redirect_uri +
      (req.response_type === "token" ? "#" : "?") +
      new URLSearchParams(params).toString();
  };

  const onDeny = () => {
    setSubmitted(true);
    redirect({ error: "access_denied" });
  };

  const onAllow = () => {
    setSubmitted(true);

    const handleApiError = (e: unknown) => {
      const error = e as { status?: number; message?: string };
      const { status } = error;
      let { message = "invalid_request" } = error;
      if (status === 500) {
        message = "server_error";
      }
      if (status === 503) {
        message = "temporarily_unavailable";
      }
      redirect({ error: message });
    };

    const params = {
      ...req,
      live_account_id: liveAccountId,
      paper_account_id: paperAccountId,
    };

    switch (req?.response_type) {
      case "code":
        authorize(params)
          .then((res: { code: string }) => {
            const { code } = res;
            redirect({ code });
          })
          .catch(handleApiError);
        break;
      case "token":
        token(params)
          .then((res: { access_token: string; token_type: string }) => {
            const { access_token, token_type } = res;
            redirect({ access_token, token_type });
          })
          .catch(handleApiError);
        break;
    }
  };

  const onClick = (accountId: string, type: string) => {
    if (type === "live") {
      setLiveAccountId(accountId);
    } else {
      setPaperAccountId(accountId);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center sm:p-8">
        <Helmet title="Authorize" />
        <div className="relative rounded-lg border-gray-300 p-2 sm:mx-auto sm:max-w-lg sm:border sm:p-8 sm:shadow-sm">
          <div className="flex flex-col items-start space-y-8">
            <h1 className="font-semibold text-2xl text-black">
              An error has occurred
            </h1>
            <span className="text-black text-md leading-5">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const ClientLink = () => (
    <a
      href={client.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-inherit underline"
    >
      {client.name}
    </a>
  );

  const isAccountsSelected = !env
    ? !!liveAccountId && !!paperAccountId
    : env === "live"
      ? !!liveAccountId
      : env === "paper"
        ? !!paperAccountId
        : false;

  return (
    <div className="flex flex-col items-center justify-center sm:p-8">
      <Helmet title="Authorize" />
      <div className="relative rounded-lg border-gray-300 p-2 sm:max-w-xl sm:border sm:p-8 sm:shadow-sm">
        <div className="flex flex-col items-start gap-4">
          <h1 className="font-semibold text-2xl">Authorize {client.name}</h1>
          <div>
            By allowing <ClientLink /> to access your Alpaca account, you are
            granting <ClientLink /> access to your account information and
            authorization to place transactions at your direction.
          </div>
          <div>
            Alpaca does not warrant or guarantee that <ClientLink /> will work
            as advertised or expected. Before authorizing, learn more about{" "}
            <ClientLink />.
          </div>
          {(client.terms_of_use || client.privacy_policy) && (
            <ul className="list-inside list-disc">
              {client.terms_of_use && (
                <li>
                  <a
                    href={client.terms_of_use}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-inherit underline"
                  >
                    Terms of Use
                  </a>
                </li>
              )}
              {client.privacy_policy && (
                <li>
                  <a
                    href={client.privacy_policy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-inherit underline"
                  >
                    Privacy Policy
                  </a>
                </li>
              )}
            </ul>
          )}
          {(!env || env === "live") && (
            <div className="flex flex-col gap-3">
              <h2 className="font-semibold text-xl">
                Select an Alpaca Account to authorize:
              </h2>
              <div>
                To authorize the use of <ClientLink />, you will need to
                authorize each account separately.
              </div>
              {liveAccounts?.map((liveAccount) => {
                const isSelected = liveAccount.id === liveAccountId;
                return (
                  <div
                    key={liveAccount.id}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={`Live Account${
                      liveAccount.account_number
                        ? ` ${liveAccount.account_number}`
                        : ""
                    }`}
                    tabIndex={0}
                    onClick={() => onClick(liveAccount.id, "live")}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        onClick(liveAccount.id, "live");
                      }
                    }}
                    className="flex cursor-pointer items-center gap-4 rounded-lg border border-gray-300 p-2"
                  >
                    <Checkbox
                      checked={isSelected}
                      onClick={() => onClick(liveAccount.id, "live")}
                      size="sm"
                    />
                    <span>
                      Live Account
                      {liveAccount.account_number
                        ? ` (${liveAccount.account_number})`
                        : ""}{" "}
                      <span className="text-gray-400">
                        {liveAccount.account_name}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {(!env || env === "paper") && (
            <div className="flex flex-col gap-3">
              <h2 className="font-semibold text-xl">
                Select an Alpaca Paper Account to authorize:
              </h2>
              <div>
                To authorize the use of <ClientLink />, you will need to
                authorize each account separately.
              </div>
              {paperAccounts?.map((paperAccount, index) => {
                const isSelected = paperAccount.id === paperAccountId;
                return (
                  <div
                    key={paperAccount.id}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={`Paper Account${
                      paperAccount.account_number
                        ? ` ${paperAccount.account_number}`
                        : ""
                    }`}
                    tabIndex={0}
                    onClick={() => onClick(paperAccount.id, "paper")}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        onClick(paperAccount.id, "paper");
                      }
                    }}
                    className="flex cursor-pointer items-center gap-4 rounded-lg border border-gray-300 p-2"
                  >
                    <Checkbox
                      checked={isSelected}
                      onClick={() => onClick(paperAccount.id, "paper")}
                      size="sm"
                    />
                    <span>
                      Paper
                      {paperAccount.account_number
                        ? ` (${paperAccount.account_number})`
                        : ""}{" "}
                      <span className="text-gray-400">
                        {paperAccountsNoDetail?.[index]?.name ?? ""}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex flex-col text-gray-600 text-sm">
            <p className="font-semibold text-black">
              By tapping &ldquo;Allow&rdquo;, you agree to the following:
            </p>
            {!client.built_by_alpaca && (
              <p>
                You understand and agree that <ClientLink /> is a third-party
                service and is not affiliated with Alpaca. Therefore, Alpaca
                cannot guarantee the availability, accuracy, or timeliness of
                services provided by <ClientLink />, including the correct and
                timely transmission of your trade orders. Alpaca is not
                responsible for any content or services provided by{" "}
                <ClientLink />, including any claims or opinions related to
                investing or the financial markets.
              </p>
            )}
            <p>
              {client.ria && (
                <>
                  Advisory services provided by <ClientLink />, an
                  SEC-registered investment advisor.{" "}
                </>
              )}
              Securities brokerage services are provided by Alpaca Securities
              LLC, member FINRA/SIPC, a wholly-owned subsidiary of AlpacaDB,
              Inc. Technology and services are offered by AlpacaDB, Inc. By
              clicking &ldquo;Allow,&rdquo; you acknowledge that Alpaca is not
              responsible for any of the services provided by <ClientLink />,
              including the trades that <ClientLink /> makes for your Alpaca
              brokerage account.
            </p>
          </div>
          <div className="flex w-full gap-4">
            <Button
              label="Deny"
              disabled={submitted}
              onClick={onDeny}
              className="flex-1"
            />
            <Button
              label="Allow"
              disabled={submitted || !isAccountsSelected}
              onClick={onAllow}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizationPage;
